import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RecipeDetails } from '@/types/recipe-details';
import { roundNutritionDisplay } from '@/utils/nutrition-display-format';

interface NutritionTabProps {
  recipeDetails: RecipeDetails;
}

/** Matches recipe modal nutrition metric cells (`RecipeDetailsModal`). */
const NUTRIENT_CELL_CLASS =
  'rounded-xl border border-[#f3f4f6] bg-white py-2 px-2 text-center shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]';

const SECTION_CARD_CLASS =
  'rounded-2xl border border-[#f3f4f6] bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)]';

function NutrientCell({ label, quantity, unit }: { label: string; quantity: string; unit: string }) {
  return (
    <div className={NUTRIENT_CELL_CLASS}>
      <div className="mb-0.5 text-xs leading-snug text-[#6b7280]">{label}</div>
      <div className="text-sm font-semibold leading-tight text-[#111827] sm:text-base">{quantity}</div>
      <div className="mt-0.5 text-xs text-[#9ca3af]">{unit}</div>
    </div>
  );
}

const formatNutritionQty = (value: number): string => roundNutritionDisplay(value);

const NutritionTab: React.FC<NutritionTabProps> = ({ recipeDetails }) => {
  const nutrition = recipeDetails.nutritionDetails || recipeDetails.nutrition;

  console.log('nutrition', nutrition, recipeDetails);
  if (!nutrition) {
    return (
      <Card className={SECTION_CARD_CLASS}>
        <CardContent className="p-8 text-center">
          <p className="text-[#6b7280]">No nutrition information available for this recipe.</p>
        </CardContent>
      </Card>
    );
  }

  const sectionTitleClass = 'text-base font-semibold tracking-[-0.02em] text-[#374151]';

  return (
    <div className="space-y-4">
      <Card className={SECTION_CARD_CLASS}>
        <CardHeader className="pb-2">
          <CardTitle className={sectionTitleClass}>Detailed macronutrients</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {nutrition.macros && (
            <div className="grid grid-cols-2 gap-1.5 md:grid-cols-3">
              {nutrition.macros.sugar && (
                <NutrientCell
                  label="Sugar"
                  quantity={formatNutritionQty(nutrition.macros.sugar.quantity)}
                  unit={nutrition.macros.sugar.unit}
                />
              )}
              {nutrition.macros.sodium && (
                <NutrientCell
                  label="Sodium"
                  quantity={formatNutritionQty(nutrition.macros.sodium.quantity)}
                  unit={nutrition.macros.sodium.unit}
                />
              )}
              {nutrition.macros.cholesterol && (
                <NutrientCell
                  label="Cholesterol"
                  quantity={formatNutritionQty(nutrition.macros.cholesterol.quantity)}
                  unit={nutrition.macros.cholesterol.unit}
                />
              )}
              {nutrition.macros.saturatedFat && (
                <NutrientCell
                  label="Saturated fat"
                  quantity={formatNutritionQty(nutrition.macros.saturatedFat.quantity)}
                  unit={nutrition.macros.saturatedFat.unit}
                />
              )}
              {nutrition.macros.transFat && (
                <NutrientCell
                  label="Trans fat"
                  quantity={formatNutritionQty(nutrition.macros.transFat.quantity)}
                  unit={nutrition.macros.transFat.unit}
                />
              )}
              {nutrition.macros.monounsaturatedFat && (
                <NutrientCell
                  label="Monounsaturated fat"
                  quantity={formatNutritionQty(nutrition.macros.monounsaturatedFat.quantity)}
                  unit={nutrition.macros.monounsaturatedFat.unit}
                />
              )}
              {nutrition.macros.polyunsaturatedFat && (
                <NutrientCell
                  label="Polyunsaturated fat"
                  quantity={formatNutritionQty(nutrition.macros.polyunsaturatedFat.quantity)}
                  unit={nutrition.macros.polyunsaturatedFat.unit}
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {nutrition.micros?.minerals && (
        <Card className={SECTION_CARD_CLASS}>
          <CardHeader className="pb-2">
            <CardTitle className={sectionTitleClass}>Minerals</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 gap-1.5 md:grid-cols-3">
              {nutrition.micros.minerals.iron && (
                <NutrientCell
                  label="Iron"
                  quantity={formatNutritionQty(nutrition.micros.minerals.iron.quantity)}
                  unit={nutrition.micros.minerals.iron.unit}
                />
              )}
              {nutrition.micros.minerals.zinc && (
                <NutrientCell
                  label="Zinc"
                  quantity={formatNutritionQty(nutrition.micros.minerals.zinc.quantity)}
                  unit={nutrition.micros.minerals.zinc.unit}
                />
              )}
              {nutrition.micros.minerals.calcium && (
                <NutrientCell
                  label="Calcium"
                  quantity={formatNutritionQty(nutrition.micros.minerals.calcium.quantity)}
                  unit={nutrition.micros.minerals.calcium.unit}
                />
              )}
              {nutrition.micros.minerals.magnesium && (
                <NutrientCell
                  label="Magnesium"
                  quantity={formatNutritionQty(nutrition.micros.minerals.magnesium.quantity)}
                  unit={nutrition.micros.minerals.magnesium.unit}
                />
              )}
              {nutrition.micros.minerals.phosphorus && (
                <NutrientCell
                  label="Phosphorus"
                  quantity={formatNutritionQty(nutrition.micros.minerals.phosphorus.quantity)}
                  unit={nutrition.micros.minerals.phosphorus.unit}
                />
              )}
              {nutrition.micros.minerals.potassium && (
                <NutrientCell
                  label="Potassium"
                  quantity={formatNutritionQty(nutrition.micros.minerals.potassium.quantity)}
                  unit={nutrition.micros.minerals.potassium.unit}
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {nutrition.micros?.vitamins && (
        <Card className={SECTION_CARD_CLASS}>
          <CardHeader className="pb-2">
            <CardTitle className={sectionTitleClass}>Vitamins</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 gap-1.5 md:grid-cols-3">
              {nutrition.micros.vitamins.vitaminA && (
                <NutrientCell
                  label="Vitamin A"
                  quantity={formatNutritionQty(nutrition.micros.vitamins.vitaminA.quantity)}
                  unit={nutrition.micros.vitamins.vitaminA.unit}
                />
              )}
              {nutrition.micros.vitamins.vitaminB6 && (
                <NutrientCell
                  label="Vitamin B6"
                  quantity={formatNutritionQty(nutrition.micros.vitamins.vitaminB6.quantity)}
                  unit={nutrition.micros.vitamins.vitaminB6.unit}
                />
              )}
              {nutrition.micros.vitamins.vitaminB12 && (
                <NutrientCell
                  label="Vitamin B12"
                  quantity={formatNutritionQty(nutrition.micros.vitamins.vitaminB12.quantity)}
                  unit={nutrition.micros.vitamins.vitaminB12.unit}
                />
              )}
              {nutrition.micros.vitamins.vitaminC && (
                <NutrientCell
                  label="Vitamin C"
                  quantity={formatNutritionQty(nutrition.micros.vitamins.vitaminC.quantity)}
                  unit={nutrition.micros.vitamins.vitaminC.unit}
                />
              )}
              {nutrition.micros.vitamins.vitaminD && (
                <NutrientCell
                  label="Vitamin D"
                  quantity={formatNutritionQty(nutrition.micros.vitamins.vitaminD.quantity)}
                  unit={nutrition.micros.vitamins.vitaminD.unit}
                />
              )}
              {nutrition.micros.vitamins.vitaminE && (
                <NutrientCell
                  label="Vitamin E"
                  quantity={formatNutritionQty(nutrition.micros.vitamins.vitaminE.quantity)}
                  unit={nutrition.micros.vitamins.vitaminE.unit}
                />
              )}
              {nutrition.micros.vitamins.vitaminK && (
                <NutrientCell
                  label="Vitamin K"
                  quantity={formatNutritionQty(nutrition.micros.vitamins.vitaminK.quantity)}
                  unit={nutrition.micros.vitamins.vitaminK.unit}
                />
              )}
              {nutrition.micros.vitamins.folate && (
                <NutrientCell
                  label="Folate"
                  quantity={formatNutritionQty(nutrition.micros.vitamins.folate.quantity)}
                  unit={nutrition.micros.vitamins.folate.unit}
                />
              )}
              {nutrition.micros.vitamins.niacin && (
                <NutrientCell
                  label="Niacin"
                  quantity={formatNutritionQty(nutrition.micros.vitamins.niacin.quantity)}
                  unit={nutrition.micros.vitamins.niacin.unit}
                />
              )}
              {nutrition.micros.vitamins.riboflavin && (
                <NutrientCell
                  label="Riboflavin"
                  quantity={formatNutritionQty(nutrition.micros.vitamins.riboflavin.quantity)}
                  unit={nutrition.micros.vitamins.riboflavin.unit}
                />
              )}
              {nutrition.micros.vitamins.thiamin && (
                <NutrientCell
                  label="Thiamin"
                  quantity={formatNutritionQty(nutrition.micros.vitamins.thiamin.quantity)}
                  unit={nutrition.micros.vitamins.thiamin.unit}
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NutritionTab;
